//*******************//
// Tabla de Usuarios //
//*******************//
import React, { useState, useEffect, useCallback } from 'react';
import { Select, MenuItem, FormControl, InputLabel, Button, TextField,
         Box, Modal, Typography, Tooltip, Divider } from '@mui/material';
import axios from 'axios';

import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import { ThemeProvider, useTheme } from '@mui/material/styles';

import { CustomToolbar } from "../../components/datagrid/CustomToolbar"; // DataGrid
import { getCustomTheme } from "../../theme/customTheme";                // Tema

import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.css';
import { jwtDecode } from 'jwt-decode';
import { RiAddBoxLine, RiFolderDownloadLine, RiShareBoxFill, RiFileEditLine,
         RiDeleteBin6Line, RiSave3Fill, RiReplyAllLine, RiReplyAllFill } from "react-icons/ri";

const Usuarios = ({ setTitle, isSidebarOpen, modo }) => {
  // variable leida desde archivo .env o .env.production //
  const apiUrl = process.env.REACT_APP_API_URL;

  // Uso del Tema //
  const themeBase = useTheme();
  const customTheme = getCustomTheme(themeBase, modo);
  const isDark = modo === "dark";

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [id_usr, setIdUsr] = useState(0);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(''); // Para mostrar el mensaje de error usuario ya existe //

  const [logUsr, setLogUsr] = useState('');
  const [pasUsr, setPasUsr] = useState('');
  const [nomUsr, setNomUsr] = useState('');
  const [estUsr, setEstUsr] = useState('');
  const [rolUsr, setRolUsr] = useState('');
  const [tipoUsr, setTipoUsr] = useState('');

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  // Cambio de titulo //
  useEffect(() => {
    setTitle(' Usuarios');
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setTitle]);

  // Obtiene el ancho disponible para la tabla considerando el sidebar
  const availableWidth = isSidebarOpen ? windowWidth - 220 : windowWidth;

  // Función para verificar la validez del token
  const isTokenValid = (token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000; // Obtener el tiempo actual en segundos
      return decoded.exp >= currentTime; // Retorna si el token no ha expirado
    } catch (error) {
      return false; // Si el token no es válido, retornar false
    }
  };

  // Función para manejar la expiración del token y redirigir al login
  const handleTokenExpiration = useCallback(() => {
    Swal.fire({
      icon: 'error',
      title: 'Sesión expirada',
      text: 'El token ha expirado. Por favor, inicia sesión nuevamente.',
      footer: '▶️ Overwatch',
      background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
      color: '#f8fafc',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        htmlContainer: 'swal-custom-text'
      },
      showConfirmButton: false,
      timer: 1500
    });
    sessionStorage.removeItem('authToken'); // Limpiar el token de sessionStorage
    // window.location.href = '/login'; // Redirigir al login
    setTimeout(() => {
      window.location.href = '/login'; // Redirigir al login //
    }, 1600); // Un retraso ligeramente mayor que el timer de SweetAlert2 //
  }, []); // Aquí pasamos un array vacío porque `handleTokenExpiration` no depende de nada en particular

  // Asignar el valor del usuario del token al RUT //
  useEffect(() => {
    const token = sessionStorage.getItem('authToken');

    if (!token || !isTokenValid(token)) {
      handleTokenExpiration(); // Manejar la expiración del token y redirigir
      return; // Evitar continuar con el código si el token es inválido o expirado
    }
  }, [handleTokenExpiration]);

  // Función para cancelar y limpiar los arreglos //
  const handleCancel = () => {
    setUsuarios([]);
    fetchUsuarios();
  };

  // cargar todos los registros de la tabla usuarios //
  const fetchUsuarios = useCallback(async () => {
    try {
      // Obtener el token desde sessionStorage //
      const token = sessionStorage.getItem('authToken');

      if (!token || !isTokenValid(token)) {
        handleTokenExpiration(); // Manejar la expiración del token y redirigir
        return; // Evitar continuar con el código si el token es inválido o expirado
      }

      setLoading(true);

      const response = await axios.post( `${apiUrl}/usuarios/getusuarios`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,  // Incluir el token en el encabezado Authorization
          },
        }
      );

      if (response.data.success && response.data.rows) {
        setUsuarios(response.data.rows); // Asigna los datos al estado //

      } else {
        setUsuarios([]); // Si no hay datos, asegúrate de limpiar el estado //
      }

    } catch (error) {
      await Swal.fire({
        icon: 'info',
        title: 'No hay datos para mostrar',
        footer: '▶️ Overwatch',
        background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
        color: '#f8fafc',
        customClass: {
          popup: 'swal-custom-popup',
          title: 'swal-custom-title',
          htmlContainer: 'swal-custom-text'
        },
        showConfirmButton: false,
        timer: 1500
      });
    } finally {
      setLoading(false);
    }
  }, [setUsuarios, apiUrl, handleTokenExpiration]);

  // llamar a cargar usuarios //
  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  // Validación del Rut (Módulo 11) //
  const validarRut = (rut) => {
    let valor = rut.replace(/\./g, '').replace('-', '');
    let cuerpo = valor.slice(0, -1);
    let dv = valor.slice(-1).toUpperCase();
    let suma = 0;
    let multiplo = 2;

    for (let i = 1; i <= cuerpo.length; i++) {
      let index = multiplo * valor.charAt(cuerpo.length - i);
      suma += index;
      multiplo = multiplo < 7 ? multiplo + 1 : 2;
    }

    let dvEsperado = 11 - (suma % 11);
    dvEsperado = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : dvEsperado.toString();
    return dv === dvEsperado;
  };


  const handleInputChange = async (e, fieldName) => {
    const { value } = e.target;

    // Actualizamos el estado del campo correspondiente //
    switch (fieldName) {
      case 'logUsr':
        setLogUsr(value);

        setErrors(prevErrors => ({
          ...prevErrors,
          logUsr: '' // Limpiar el error del RUT al escribir //
        }));

        // Validación del RUT //
        if (!validarRut(value)) {
          setMessage('');
          setErrors(prevErrors => ({
            ...prevErrors,
            logUsr: 'El Rut no es válido'
          }));
          return; // Si el RUT no es válido, no continuar con la verificación de existencia //
        }

        // Verificar si el RUT ya existe en la base de datos success = false //
        try {
          // Rellenar con ceros a la izquierda hasta que tenga un largo de 10 //
          const fixlogUsr = value.padStart(10, '0');

          // Verificar si el RUT ya existe en la base de datos
          const rutExists = await checkRutExists(fixlogUsr);

          // Revisar si el campo success en la respuesta es true //
          if (rutExists) {
            setMessage('El Rut ya existe en la Bd.'); // Mostrar mensaje si el RUT ya existe //
          } else {
            // Limpiar el mensaje si el RUT no existe //
            setMessage('');
          }

        } catch (error) {
          setMessage('Hubo un problema al verificar el RUT'); // Mensaje de error en caso de fallo en la verificación //
        }
        break;

        case 'pasUsr':
          setPasUsr(value);
          setErrors(prevErrors => ({
            ...prevErrors,
            pasUsr: '' // Limpiar el error de la contraseña //
          }));
          break;

        case 'nomUsr':
          setNomUsr(value);
          setErrors(prevErrors => ({
            ...prevErrors,
            nomUsr: '' // Limpiar el error del nombre //
          }));
          break;

        case 'estUsr':
          setEstUsr(value);
          setErrors(prevErrors => ({
            ...prevErrors,
            estUsr: '' // Limpiar el error del estado //
          }));
          break;

        case 'rolUsr':
          setRolUsr(value);
          setErrors(prevErrors => ({
            ...prevErrors,
            rolUsr: '' // Limpiar el error del rol //
          }));
          break;

        case 'tipoUsr':
          setTipoUsr(value);
          setErrors(prevErrors => ({
            ...prevErrors,
            tipoUsr: '' // Limpiar el error del tipo //
          }));
          break;

      default:
        break;
    }

    // Limpiamos el error para este campo //
    setErrors(prevErrors => ({
      ...prevErrors,
      [fieldName]: '' // Borra el error para el campo correspondiente //
    }));
  };

  // Verificar si el RUT ya existe en la base de datos
  const checkRutExists = async (rut) => {
    try {
      const token = sessionStorage.getItem('authToken');

      if (!token || !isTokenValid(token)) {
        handleTokenExpiration(); // Manejar la expiración del token y redirigir
        return; // Evitar continuar con el código si el token es inválido o expirado
      }

      // Rellenar con ceros a la izquierda hasta que tenga un largo de 10 //
      const RutUsr = rut.padStart(10, '0');

      const data = await axios.post( `${apiUrl}/clientes/checkrut`,
        { RutUsr },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      return data.data.success;

    } catch (error) {
      return false; // Si hubo un error, retornar false //
    }
  };

  // Agregar o Editar usuario //
  const handleSave = async () => {
    const validationErrors = {};

    if (!logUsr) validationErrors.logUsr = 'El campo Usuario es obligatorio';
    if (!pasUsr) validationErrors.pasUsr = 'El campo Contraseña es obligatorio';
    if (!nomUsr) validationErrors.nomUsr = 'El campo Nombre es obligatorio';
    if (!estUsr) validationErrors.estUsr = 'El campo Estado es obligatorio';
    if (!rolUsr) validationErrors.rolUsr = 'El campo Rol es obligatorio';
    if (!tipoUsr) validationErrors.tipoUsr = 'El campo Tipo es obligatorio';

    if (!validarRut(logUsr)) {
      validationErrors.logUsr = 'El Rut no es válido';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Rellenar con ceros a la izquierda hasta que logUsr tenga un largo de 10 //
    const fixlogUsr = logUsr.padStart(10, '0');

    // Convertir nomUsr a mayúsculas //
    const uppernomUsr = nomUsr.toUpperCase();

    // update del registro //
    try {
      if (editingUsuario) {
        // Obtener el token desde sessionStorage //
        const token = sessionStorage.getItem('authToken');

        if (!token || !isTokenValid(token)) {
          handleTokenExpiration(); // Manejar la expiración del token y redirigir
          return; // Evitar continuar con el código si el token es inválido o expirado
        }

        await axios.post( `${apiUrl}/usuarios/updateusuario`,
          {
            id_usr: id_usr,
            nombre_usr: uppernomUsr,
            estado_usr: estUsr,
            rol_usr: rolUsr,
            tipo_usr: tipoUsr
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`} // Pasar el token en los encabezados //
          }
        );
        // insert registro //
      } else {
        // Verificar si el RUT ya existe en la base de datos
        const rutExists = await checkRutExists(fixlogUsr);

        // Revisar si el campo success en la respuesta es true
        if (rutExists) {
          // Si el servidor responde que la validacion no fue exitosa
          setMessage('Cliente Ya Existe. handleSave');
          setErrors({});
          return;
        }

        // Obtener el token desde sessionStorage //
        const token = sessionStorage.getItem('authToken');

        if (!token || !isTokenValid(token)) {
          handleTokenExpiration(); // Manejar la expiración del token y redirigir
          return; // Evitar continuar con el código si el token es inválido o expirado
        }

        await axios.post( `${apiUrl}/usuarios/addusuario`,
          {
            usuario_usr: fixlogUsr,
            password_usr: pasUsr,
            nombre_usr: uppernomUsr,
            estado_usr: estUsr,
            rol_usr: rolUsr,
            tipo_usr: tipoUsr
          },
          {
            headers: {
              'Authorization': `Bearer ${token}` // Pasar el token en los encabezados //
            }
          }
        );
      }

      if (editingUsuario) {
        Swal.fire({
          icon: "success",
          title: "Registro Regrabado",
          text: "✅ El Usuario fue actualizado correctamente.",
          footer: '▶️ Overwatch',
          confirmButtonColor: "#3085d6",
          allowOutsideClick: false,  // 👈 evita cierre con click afuera
          background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
          color: '#f8fafc',
          customClass: {
            popup: 'swal-custom-popup',
            title: 'swal-custom-title',
            htmlContainer: 'swal-custom-text'
          }
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Registro Grabado",
          text: "✅ El Usuario fue registrado correctamente.",
          footer: '▶️ Overwatch',
          confirmButtonColor: "#3085d6",
          allowOutsideClick: false,  // 👈 evita cierre con click afuera
          background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
          color: '#f8fafc',
          customClass: {
            popup: 'swal-custom-popup',
            title: 'swal-custom-title',
            htmlContainer: 'swal-custom-text'
          }
        });
      }

      fetchUsuarios();

      // 👇 Desenfoca cualquier elemento activo dentro del modal
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // 👇 Esperar a que el Swal termine antes de cerrar el modal MUI
      setTimeout(() => {
        handleCloseModal();
      }, 100);

    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        footer: '▶️ Overwatch',
        text: "Error al grabar el registro...!!!",
        allowOutsideClick: false,  // 👈 evita cierre con click afuera
        background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
        color: '#f8fafc',
        customClass: {
          popup: 'swal-custom-popup',
          title: 'swal-custom-title',
          htmlContainer: 'swal-custom-text'
        }
      });
    }
  };

  // eliminar registro //
  const handleDelete = async (id_usr, usuario_usr, nombre_usr) => {
    const result = await Swal.fire({
      title: "¿Está seguro de eliminar?",
      footer: '▶️ Overwatch',
      allowOutsideClick: false,  // 👈 evita cierre con click afuera
      background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
      color: '#f8fafc',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        htmlContainer: 'swal-custom-text'
      },
      html: `
        <b>Id: ${id_usr}</b><br>
        <b>Rut: ${usuario_usr}</b><br>
        <b>Nombre: ${nombre_usr}</b>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar!",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        // Obtener el token desde sessionStorage //
        const token = sessionStorage.getItem('authToken');

        if (!token || !isTokenValid(token)) {
          handleTokenExpiration(); // Manejar la expiración del token y redirigir
          return; // Evitar continuar con el código si el token es inválido o expirado
        }

        await axios.post( `${apiUrl}/usuarios/deleteusuario`,
        { id_usr: id_usr },
        {
          headers: {
            'Authorization': `Bearer ${token}` // Pasar el token en los encabezados //
          }
        });

        Swal.fire({
          title: "¡Eliminado!",
          text: "El registro ha sido eliminado correctamente.",
          footer: '▶️ Overwatch',
          icon: "success",
          allowOutsideClick: false,  // 👈 evita cierre con click afuera
          background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
          color: '#f8fafc',
          customClass: {
            popup: 'swal-custom-popup',
            title: 'swal-custom-title',
            htmlContainer: 'swal-custom-text'
          }
        });

        fetchUsuarios();

      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          footer: '▶️ Overwatch',
          text: "Error al eliminar el registro...!!!",
          allowOutsideClick: false,  // 👈 evita cierre con click afuera
          background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
          color: '#f8fafc',
          customClass: {
            popup: 'swal-custom-popup',
            title: 'swal-custom-title',
            htmlContainer: 'swal-custom-text'
          }
        });
      }
    }
  };

  // Abrir Modal para Editar usuario //
  const handleEdit = (usuario) => {
    setIdUsr(usuario.id_usr);
    setLogUsr(usuario.usuario_usr);
    setPasUsr(usuario.password_usr);
    setNomUsr(usuario.nombre_usr);
    setEstUsr(usuario.estado_usr);
    setRolUsr(usuario.rol_usr);
    setTipoUsr(usuario.tipo_usr);
    setEditingUsuario(usuario);
    setMessage('');
    setOpen(true);

    // Limpiar el error de todos los input //
    setErrors(prevErrors => ({
     ...prevErrors,
     id: 0,
     logUsr: '',
     pasUsr: '',
     nomUsr: '',
     estUsr: '',
     rolUsr: '',
     tipoUsr: '',
   }));
  };

  // Abrir Modal para Crear usuario //
  const handleAdd = () => {
    setLogUsr('');
    setPasUsr('');
    setNomUsr('');
    setEstUsr('');
    setRolUsr('');
    setTipoUsr('');
    setEditingUsuario(null);
    setMessage('');
    setOpen(true);

    // Limpiar el error de todos los input //
    setErrors(prevErrors => ({
      ...prevErrors,
      logUsr: '',
      pasUsr: '',
      nomUsr: '',
      estUsr: '',
      rolUsr: '',
      tipoUsr: '',
    }));
  };

  // estado para modal //
  const handleCloseModal = () => setOpen(false);

  // exportar a pdf todos los datos de la tabla //
  const logo = new Image();
  logo.src = '/Overwatch.jpg'; // Ruta del logo //

  const exportToPDF = () => {
    const doc = new jsPDF('landscape');

    // Añadir logo //
    const logoX = 5; // Posición X del logo
    const logoY = 5; // Posición Y del logo
    doc.addImage(logo, "JPEG", logoX, logoY, 60, 10);

    // Fecha y página alineados a la derecha //
    doc.setFontSize(10);
    const fecha = `Fecha: ${new Date().toLocaleDateString()}`;
    const pagina = `Página 1 de 1`;
    const pageWidth = doc.internal.pageSize.width;

    // Calcular posición de la fecha y la página //
    const fechaX = pageWidth - doc.getTextWidth(fecha) - 5; // 10 de margen //
    const paginaX = pageWidth - doc.getTextWidth(pagina) - 5; // 10 de margen //

    // Colocar fecha y página en la misma línea que el logo //
    const textY = logoY + 10; // Alinear verticalmente con el logo //
    const paginaY = textY + 5; // Colocar la paginación 5 unidades más abajo //

    doc.text(fecha, fechaX, textY);
    doc.text(pagina, paginaX, paginaY); // Usar nueva posición Y para la paginación //

    // Título //
    doc.setFontSize(18);
    doc.text('Listado de Usuarios', 120, 25); // horizontal, vertical //

    // Tabla de Usuarios //
    doc.autoTable({
      head: [['USUARIO', 'NOMBRE']],
      body: usuarios.map((usuario) => [usuario.usuario_usr, usuario.nombre_usr]),
      foot: [['Total', usuarios.length]],
      startY: 30, // Comenzar la tabla después de los encabezados //
      styles: {
        fontSize: 12,
        cellPadding: 3,
        halign: 'center',
      },
      headStyles: {
        fillColor: [22, 160, 133], // Color de encabezado //
        textColor: [255, 255, 255], // Color de texto en encabezado //
      },
      footStyles: {
        fillColor: [22, 160, 133], // Color de pie de página //
        textColor: [255, 255, 255], // Color de texto en pie de página //
      },
    });

    // Total de registros //
    doc.text(`Registros: ${usuarios.length}`, 10, doc.internal.pageSize.height - 10);
    doc.save('usuarios.pdf');
  };

  logo.onerror = () => {
    console.error('Error al cargar la imagen del logo.', logo.src);
  };


  // Mostrar columnas en MaterialReactTable //
  const columns = [
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const row = params.row;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            
            {/* --- Botón Editar (Azul) --- */}
            <Tooltip title="Editar" placement="top" arrow>
              <Button
                onClick={() => handleEdit(row)}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: '#1E88E5',    // azul
                  color: 'white',                // icono blanco
                  minWidth: 0,
                  padding: 0,
                  width: 30,
                  height: 22,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: '#1565C0', // azul más oscuro
                  },
                }}
              >
                <RiFileEditLine size={18} />
              </Button>
            </Tooltip>

            {/* --- Botón Eliminar (Rojo) --- */}
            <Tooltip title="Eliminar" placement="top" arrow>
              <Button
                onClick={() => handleDelete(row.id_usr, row.usuario_usr, row.nombre_usr)}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: '#E53935',   // rojo
                  color: 'white',               // icono blanco
                  minWidth: 0,
                  padding: 0,
                  width: 30,
                  height: 22,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: '#C62828', // rojo más oscuro
                  },
                }}
              >
                <RiDeleteBin6Line size={18} />
              </Button>
            </Tooltip>

          </div>
        );
      },
      headerClassName: 'acciones-header',
    },
    {
      field: 'id_usr',
      width: 80,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Id del Registro" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Id
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'usuario_usr',
      headerName: "Usuario",
      width: 130,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Usuario" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Usuario
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'nombre_usr',
      headerName: "Nombre",
      width: 400,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Nombre del Usuario" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Nombre
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'estado_usr',
      headerName: "Estado",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Estado del Usuario" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Estado
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'rol_usr',
      headerName: "Rol",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Rol del Usuario" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Rol
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'tipo_usr',
      headerName: "Tipo",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Tipo de Usuario" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Tipo
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'spacer',
      headerName: '',
      width: 355,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,   // ❗ elimina los 3 puntos
      disableReorder: true,      // evita que se arrastre
      disableExport: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: () => null,    // ❗ evita que se vea algo raro
    }
  ];

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mt: 2,
          mb: 1,
          height: '70px',
          flexWrap: 'wrap', // Permite que los elementos se acomoden en múltiples líneas si es necesario
          maxWidth: '100%',
        }}
      >
        <Tooltip title="Agregar Nuevo Registro" placement="top" arrow>
          <Button variant="contained"
            onClick={handleAdd}
            sx={{
              width: '160px',
              height: '40px',
              textTransform: 'none',
              backgroundColor: '#8b888e', // Color de fondo inicial
              color: 'white', // Color del texto en blanco
              '&:hover': {
                backgroundColor: '#65a76c', // Cambia el color de fondo a verde al pasar el mouse //
                color: 'white', // Cambia el color del texto a blanco //
              }
            }}
          >
            <RiAddBoxLine className="m-2" style={{ fontSize: '1.5rem' }} /> {/* Aumento el tamaño del icono */}
            Agregar
          </Button>
        </Tooltip>

        {/* Botón Cancelar */}
        <Tooltip title="Cancelar Todo" placement="top" arrow>
          <Button
            variant="contained"
            onClick={handleCancel}
            sx={{
              width: '180px',
              height: '40px',
              textTransform: 'none',
              backgroundColor: '#8b888e', // Color de fondo para cancelar
              color: 'white',
              '&:hover': {
                backgroundColor: '#b9770e', // Color al pasar el cursor //
                color: 'white',
              },
              '& .MuiButton-startIcon': {
                color: 'white',
              },
            }}
          >
            {/* Icono de cancelar */}
            <RiReplyAllFill className="m-2" style={{ fontSize: '1.5rem' }} />
            Cancelar
          </Button>
        </Tooltip>

        <Tooltip title="Exportar a Pdf" placement="top" arrow>
          <Button variant="contained"
            onClick={exportToPDF}
            sx={{
              width: '100px',
              height: '40px',
              backgroundColor: '#8b888e', // Color de fondo inicial
              color: 'white', // Color del texto en blanco
              '&:hover': {
                backgroundColor: '#65a76c', // Cambia el color de fondo a verde al pasar el mouse //
                color: 'white', // Cambia el color del texto a blanco //
              }
            }}
          >
            <RiFolderDownloadLine className="m-2" style={{ fontSize: '1.5rem' }} /> {/* Aumento el tamaño del icono */}
            PDF
          </Button>
        </Tooltip>

        <Tooltip title="Exportar a Csv" placement="top" arrow>
          <Button variant="contained"
            sx={{
              width: '100px',
              height: '40px',
              backgroundColor: '#8b888e', // Color de fondo inicial
              color: 'white', // Color del texto en blanco
              '&:hover': {
                backgroundColor: '#65a76c', // Cambia el color de fondo a verde al pasar el mouse //
                color: 'white', // Cambia el color del texto a blanco //
              }
            }}
          >
            <RiShareBoxFill className="m-2" style={{ fontSize: '1.5rem' }} /> {/* Aumento el tamaño del icono */}
            <CSVLink data={usuarios} filename="usuarios.csv">
              CSV
            </CSVLink>
          </Button>
        </Tooltip>

        {/* Configuración MIU DataGrid */}
        <Box
          sx={{
            display: 'block',
            boxSizing: 'border-box',
            p: '1px',
            maxWidth: '98%',
            width: availableWidth, // Ajuste dinámico según sidebar
            overflowX: 'auto',
          }}
        >
          <Box sx={{ mt: 1 }}>
            <ThemeProvider theme={customTheme}>
              <DataGrid
                rows={usuarios}
                columns={columns}
                getRowId={(row) => row.id_usr}
                loading={loading}
                density="compact"
                rowHeight={38}
                headerHeight={40}
                pagination
                initialState={{
                  pagination: { paginationModel: { pageSize: 10, page: 0 } },
                  density: 'compact',
                }}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                slots={{
                  toolbar: CustomToolbar,
                }}
                // slots={{
                //   toolbar: GridToolbar,
                // }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 400 },
                  },
                }}
                // disableRowSelectionOnClick={false}
                // rowSelectionModel={selectionModel}
                // onRowSelectionModelChange={handleSelectionChange}

                sx={{
                  border: 'none',
                  backgroundColor: modo === 'dark' ? '#2b2b2b' : '#eaeded',

                  // 🎨 CABECERA
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: modo === 'dark' ? '#85c1e9 !important' : '#85c1e9 !important',
                  },
                  '& .MuiDataGrid-columnHeadersInner': {
                    backgroundColor: modo === 'dark' ? '#85c1e9 !important' : '#85c1e9 !important',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: modo === 'dark' ? '#85c1e9 !important' : '#85c1e9 !important',
                    color: modo === 'dark' ? '#000 !important' : '#000 !important',
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    borderBottom: '2px solid #a9cce3',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                    textAlign: 'center',
                    width: '100%',
                  },

                  // 🔹 permite respetar la alineación por columna
                  '& .MuiDataGrid-cell--textLeft': {
                    justifyContent: 'flex-start !important',
                    textAlign: 'left !important',
                  },
                  '& .MuiDataGrid-cell--textCenter': {
                    justifyContent: 'center !important',
                    textAlign: 'center !important',
                  },
                  '& .MuiDataGrid-cell--textRight': {
                    justifyContent: 'flex-end !important',
                    textAlign: 'right !important',
                  },

                  // 🧾 CELDAS
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    lineHeight: 1,
                    textAlign: 'center',
                    p: '2px 8px',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    color: modo === 'dark' ? '#B1AAAE' : '#000',
                  },

                  // 🩶 FILAS
                  '& .MuiDataGrid-row': {
                    backgroundColor: modo === 'dark' ? '#222222' : '#f2f3f4',
                    '&:hover': {
                      backgroundColor: modo === 'dark' ? '#3e3633' : '#eaf2f8',
                    },
                  },

                  // 🧭 FOOTER '#B1AAAE'
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '1px solid #ccc',
                    minHeight: '30px',
                    backgroundColor: modo === 'dark' ? '#3e3633' : '#f1f3f4',
                    color: modo === 'dark' ? '#3e3633' : '#000',
                  },

                  // 🔹 SCROLL
                  '& .MuiDataGrid-virtualScroller': {
                    maxHeight: '600px',
                  },

                  // Color de selección y le quita la caja de seleción
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none !important',
                  },
                  '& .MuiDataGrid-cell:focus-within': {
                    outline: 'none !important',
                  },
                  '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: modo === 'dark' ? '#3e3633' : '#B1AAAE !important', // color de fila seleccionada
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: modo === 'dark' ? '#3e3633' : '#B1AAAE !important', // hover opcional
                  },
                }}
              />
            </ThemeProvider>
          </Box>
        </Box>

        {/* Modal para ingresar o modificar */}
        <Modal
          open={open}
          onClose={(event, reason) => {
            // Solo cerrar si el motivo NO es 'backdropClick' ni 'escapeKeyDown'
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseModal();
            }
          }}
          >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '600px',           // ancho fijo en desktop
              maxWidth: '95vw',         // se ajusta en pantallas pequeñas
              maxHeight: '90vh',        // límite de alto
              bgcolor: isDark ? "#2b2b2b" : "#f2f3f4",   // ← agregado
              boxShadow: 24,
              p: 3,
              overflowY: 'auto',        // 👈 habilita scroll vertical
              borderRadius: 2,          // opcional: bordes redondeados
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: isDark ? "#FACC15" : "#1e293b" }}>
                {editingUsuario ? 'Editando Usuario' : 'Agregando Usuario'}
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: isDark ? "#fff" : "#1e293b" }}>
                ▶️ Overwatch
              </Typography>
            </Box>

            <Divider sx={{ marginY: 2 }} />
            <div className="mb-4" /> {/* Esta línea añade un margen inferior */}

            {/* Mostrar mensaje de error si existe */}
            {message && (
              <Typography color="error" sx={{ marginBottom: 2 }}>
                {message}
              </Typography>
            )}

            {/* USUARIO */}
            <TextField
              label="Usuario"
              value={logUsr}
              onChange={(e) => handleInputChange(e, 'logUsr')}
              fullWidth
              sx={{
                marginBottom: 2,
                '& .MuiInputBase-root': {
                  height: 40,
                  backgroundColor: isDark ? "#3a3a3a" : undefined,   // ← agregado
                  color: isDark ? "#fff" : undefined,
                },
                '& .MuiInputLabel-root': { color: isDark ? "#ccc" : undefined },
              }}
              error={Boolean(errors.logUsr)}
              helperText={errors.logUsr}
              disabled={Boolean(editingUsuario)} // Deshabilitar si estamos editando un usuario //
              autoComplete="off" // Desactivar autocompletado //
            />

            {/* CONTRASEÑA - Solo en creación */}
            {!editingUsuario && (
              <TextField
                label="Contraseña"
                value={pasUsr}
                onChange={(e) => handleInputChange(e, 'pasUsr')}
                fullWidth
                sx={{
                  marginBottom: 2,
                  '& .MuiInputBase-root': {
                    height: 40,
                    backgroundColor: isDark ? "#3a3a3a" : undefined,   // ← agregado
                    color: isDark ? "#fff" : undefined,
                  },
                  '& .MuiInputLabel-root': { color: isDark ? "#ccc" : undefined },
                }}
                error={Boolean(errors.pasUsr)}
                helperText={errors.pasUsr}
                autoComplete="off" // Desactivar autocompletado //
              />
            )}

            {/* NOMBRE */}
            <TextField
              label="Nombre"
              value={nomUsr}
              onChange={(e) => handleInputChange(e, 'nomUsr')}
              fullWidth
              sx={{
                marginBottom: 2,
                '& .MuiInputBase-root': {
                  height: 40,
                  backgroundColor: isDark ? "#3a3a3a" : undefined,   // ← agregado
                  color: isDark ? "#fff" : undefined,
                },
                '& .MuiInputLabel-root': { color: isDark ? "#ccc" : undefined },
              }}
              error={Boolean(errors.nomUsr)}
              helperText={errors.nomUsr}
              autoComplete="off" // Desactivar autocompletado //
            />

            {/* ESTADO */}
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={estUsr}
                onChange={(e) => handleInputChange(e, 'estUsr')}
                label="Estado"
                error={Boolean(errors.estUsr)}
                sx={{
                  height: 40,
                  backgroundColor: isDark ? "#3a3a3a" : undefined,
                  color: isDark ? "#fff" : undefined,
                }}
              >
                <MenuItem value="HABILITADO">HABILITADO</MenuItem>
                <MenuItem value="DESHABILITADO">DESHABILITADO</MenuItem>
              </Select>
              {errors.estUsr && <Typography color="error" variant="body2">{errors.estUsr}</Typography>}
            </FormControl>

            {/* ROL */}
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <InputLabel>Rol</InputLabel>
              <Select
                value={rolUsr}
                onChange={(e) => handleInputChange(e, 'rolUsr')}
                label="Rol"
                error={Boolean(errors.rolUsr)}
              >
                <MenuItem value="USUARIO">USUARIO</MenuItem>
                <MenuItem value="ADMINISTRADOR">ADMINISTRADOR</MenuItem>
              </Select>
              {errors.rolUsr && <Typography color="error" variant="body2">{errors.rolUsr}</Typography>}
            </FormControl>

            {/* TIPO */}
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={tipoUsr}
                onChange={(e) => handleInputChange(e, 'tipoUsr')}
                label="Tipo"
                error={Boolean(errors.tipoUsr)}
                sx={{
                  height: 40,
                  backgroundColor: isDark ? "#3a3a3a" : undefined,
                  color: isDark ? "#fff" : undefined,
                }}
              >
                <MenuItem value="CLIENTE">CLIENTE</MenuItem>
                <MenuItem value="ENROLADO">ENROLADO</MenuItem>
                <MenuItem value="SISTEMA">SISTEMA</MenuItem>
              </Select>
              {errors.tipoUsr && <Typography color="error" variant="body2">{errors.tipoUsr}</Typography>}
            </FormControl>

            {/* display: 'flex'. Esto coloca los botones uno al lado del otro. El gap: '8px' agrega un espacio de 8 píxeles entre los botones. */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  width: '180px',
                  // padding: '6px 16px', // Ajustar padding para que los botones tengan el mismo tamaño //
                  height: '40px',
                  backgroundColor: '#8b888e', // Color de fondo inicial
                  color: 'white', // Color del texto en blanco
                  '&:hover': {
                    backgroundColor: 'red', // Cambia el color de fondo a verde al pasar el mouse //
                    color: 'white', // Cambia el color del texto a blanco //
                  }
                }}
                onClick={handleSave}>
                <RiSave3Fill className="m-2" style={{ fontSize: '1.5rem' }} /> {/* Aumento el tamaño del icono */}
                {editingUsuario ? 'Regrabar' : 'Grabar'}
              </Button>

              <Button
                variant="outlined"
                sx={{
                  width: '180px',
                  // padding: '6px 16px', // Ajustar padding para que los botones tengan el mismo tamaño //
                  height: '40px', // Establece la altura constante //
                  backgroundColor: '#8b888e', // Color de fondo inicial
                  color: 'white', // Color del texto en blanco
                  '&:hover': {
                    backgroundColor: 'green', // Cambia el color de fondo a verde al pasar el mouse //
                    color: 'white', // Cambia el color del texto a blanco //
                  }
                }}
                onClick={handleCloseModal}>
                <RiReplyAllLine className="m-2" style={{ fontSize: '1.5rem' }} /> {/* Aumento el tamaño del icono */}
                Cancelar
              </Button>
            </div>
          </Box>
        </Modal>
      </Box>
    </div>
  );
};

export default Usuarios;
